import React, { useState } from 'react';
import { formatUSNumber, parseUSNumber, formatUSPercent } from '@/utils/locale-format';

type Mode = 'number' | 'percent' | 'integer' | 'currency';

interface Props {
  value: number | null | undefined; // stored as number (decimal for percent)
  onChange: (val: number | null) => void;
  mode: Mode;
  decimals?: number; // display decimals
  step?: number; // step in display units (for percent, step is percent units)
  className?: string;
  'data-test-id'?: string;
}

export default function USFormattedNumberInput({
  value,
  onChange,
  mode,
  decimals = 2,
  step = 0.01,
  className,
  'data-test-id': dataTestId,
}: Props) {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Format for display (US separators: comma for thousands, dot for decimal)
  const display = React.useMemo(() => {
    switch (mode) {
      case 'percent':
        return formatUSPercent(value ?? null, decimals);
      case 'integer':
        return formatUSNumber(value ?? null, 0);
      case 'currency':
      case 'number':
      default:
        return formatUSNumber(value ?? null, decimals);
    }
  }, [value, mode, decimals]);

  // For percent mode: convert decimal to percent for display when focused
  const getPercentDisplayValue = (decimalValue: number | null | undefined): string => {
    if (decimalValue === null || decimalValue === undefined || Number.isNaN(decimalValue) || !isFinite(decimalValue)) {
      return '';
    }
    // Convert decimal to percent (0.0637 -> 6.37)
    const percentValue = decimalValue * 100;
    return formatUSNumber(percentValue, decimals);
  };

  // Handle input with non-removable % sign for percent mode
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const txt = e.target.value;
    if (mode === 'percent') {
      // Remove % sign for parsing, but we'll add it back
      let cleaned = txt.replace(/%/g, '').trim();
      
      // If user deletes everything, allow empty but still show % placeholder behavior
      if (cleaned === '' || cleaned === '-') {
        onChange(null);
        setTimeout(() => {
          e.target.value = '';
        }, 0);
        return;
      }
      
      // Parse the number part (percent value like 6.37)
      const num = parseUSNumber(cleaned);
      if (num === null || Number.isNaN(num)) {
        return;
      }
      
      // Convert percent to decimal (6.37 -> 0.0637) and update
      onChange(num / 100);
    } else if (mode === 'integer') {
      const n = parseUSNumber(txt);
      onChange(n === null ? null : Math.trunc(n));
    } else {
      onChange(parseUSNumber(txt));
    }
  };

  const applyStep = (direction: 1 | -1) => {
    try {
      if (mode === 'percent') {
        const current = typeof value === 'number' && !isNaN(value) ? value : 0; // decimal
        // step is percent units; convert to decimal delta
        const delta = (step || 0.01) / 100;
        onChange(Number((current + direction * delta).toFixed(Math.max(6, decimals + 2))));
      } else if (mode === 'integer') {
        const current = typeof value === 'number' && !isNaN(value) ? value : 0;
        onChange(current + direction * Math.max(1, Math.round(step || 1)));
      } else {
        const current = typeof value === 'number' && !isNaN(value) ? value : 0;
        onChange(Number((current + direction * (step || 0.01)).toFixed(Math.max(6, decimals + 2))));
      }
    } catch (e) {
      console.error('Error in applyStep:', e);
    }
  };

  // Show percent value when focused (with % sign), formatted display when blurred
  const inputValue = React.useMemo(() => {
    if (isFocused) {
      if (mode === 'percent') {
        // Show percent value with % sign (6.37%)
        const percentDisplay = getPercentDisplayValue(value);
        return percentDisplay ? `${percentDisplay}%` : '';
      } else {
        // For other modes, show raw value
        return value !== null && value !== undefined && !isNaN(Number(value)) ? value.toString() : '';
      }
    } else {
      // Show formatted display when not focused
      return display || '';
    }
  }, [isFocused, mode, value, display, decimals]);

  // Handle keydown to prevent deleting % sign and ensure it's always present
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (mode === 'percent' && isFocused) {
      const input = e.currentTarget;
      const cursorPos = input.selectionStart || 0;
      const valueLength = input.value.length;
      const hasPercent = input.value.endsWith('%');
      
      // If user tries to delete the % sign, prevent it
      if (hasPercent && 
          ((e.key === 'Backspace' && cursorPos === valueLength - 1) ||
           (e.key === 'Delete' && cursorPos === valueLength - 1))) {
        e.preventDefault();
        // Move cursor before %
        input.setSelectionRange(valueLength - 1, valueLength - 1);
      }
      
      // Prevent typing % manually (we add it automatically)
      if (e.key === '%') {
        e.preventDefault();
      }
    }
  };
  
  // Handle paste to ensure % is always present
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (mode === 'percent' && isFocused) {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      // Remove any existing % signs and add our own
      const cleaned = pastedText.replace(/%/g, '').trim();
      const num = parseUSNumber(cleaned);
      if (num !== null && !Number.isNaN(num)) {
        onChange(num / 100);
      }
    }
  };

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        type="text"
        className={`${className || ''} rounded-md shadow-sm h-10 py-2`}
        value={inputValue}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-test-id={dataTestId}
        style={{ 
          width: '100%',
          paddingLeft: '12px',
          paddingRight: isHovered ? '14px' : '12px',
        }}
      />
      {/* Native-style spinner controls - only visible on hover */}
      {isHovered && (
        <div className="absolute right-0 top-0 bottom-0 flex flex-col pointer-events-none" style={{ width: '14px' }}>
          <button
            type="button"
            className="flex-1 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors pointer-events-auto"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyStep(1);
            }}
            aria-label="increment"
            style={{
              fontSize: '8px',
              lineHeight: '1',
              color: '#6b7280',
              background: '#f3f4f6',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            ▲
          </button>
          <button
            type="button"
            className="flex-1 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 transition-colors pointer-events-auto"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyStep(-1);
            }}
            aria-label="decrement"
            style={{
              fontSize: '8px',
              lineHeight: '1',
              color: '#6b7280',
              background: '#f3f4f6',
            }}
          >
            ▼
          </button>
        </div>
      )}
    </div>
  );
}

